import { AccountApi, SuccessResponse, User } from '@citruscamps/citrus-client'
import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { generateApiConfig } from '../../../../../utils/client-config'
import { generateBaseKey } from '../../../../../utils/key-generator'
import { useRequestHandler } from '../../../../../hooks/useRequestHandler'

interface IVerifyEmail {
  handleVerifyCode: (code: string) => Promise<void>
  handleResendVerifyEmail: () => Promise<void>
}

interface IProps {
  user?: User
}

export const useVerifyEmail = ({ user }: IProps): IVerifyEmail => {
  const { requestHandler } = useRequestHandler()
  const [abortController, setAbortController] = useState<AbortController>()
  const queryClient = useQueryClient()

  const queryKey = generateBaseKey({
    type: 'verify_email',
    program_id: undefined,
    query: user,
  })
  useQuery(
    queryKey,
    async ({ signal }) => {
      const accountClient = new AccountApi(generateApiConfig())
      await requestHandler<SuccessResponse>(() => accountClient.reverifyEmailAccount({ signal }), {
        redirect: false,
      })
    },
    {
      enabled: !!user && !user?.is_email_verified,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  )

  const handleResendVerifyEmail = async () => {
    abortController?.abort()
    const controller = new AbortController()
    setAbortController(controller)
    const signal = controller.signal
    const accountClient = new AccountApi(generateApiConfig())
    await requestHandler<SuccessResponse>(() => accountClient.reverifyEmailAccount({ signal }), {
      redirect: false,
    })
  }

  const handleVerifyCode = async (code: string) => {
    const accountClient = new AccountApi(generateApiConfig())
    await requestHandler<User>(
      () => accountClient.verifyEmailAccount({ EmailVerification: { code } }),
      {
        redirect: false,
      },
    )
    await queryClient.refetchQueries('user')
  }

  useEffect(() => {
    return () => {
      abortController?.abort()
    }
  }, [abortController])

  return {
    handleResendVerifyEmail,
    handleVerifyCode,
  }
}
